import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "her", label: "For Her" },
  { key: "him", label: "For Him" },
  { key: "unisex", label: "Unisex" },
];

const SORTS = [
  { key: "newest", label: "Newest" },
  { key: "price_asc", label: "Price: Low → High" },
  { key: "price_desc", label: "Price: High → Low" },
];

export default function ShopPage() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(params.get("q") || "");

  const category = params.get("category") || "all";
  const sort = params.get("sort") || "newest";

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/products", {
          params: {
            q: params.get("q") || undefined,
            category: category === "all" ? undefined : category,
            sort,
          },
        });
        setProducts(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [params, category, sort]);

  const updateParam = (k, v) => {
    const p = new URLSearchParams(params);
    if (v == null || v === "" || v === "all") p.delete(k);
    else p.set(k, v);
    setParams(p);
  };

  const onSubmitSearch = (e) => {
    e.preventDefault();
    updateParam("q", q);
  };

  const heading = useMemo(() => {
    const c = CATEGORIES.find((c) => c.key === category);
    return c && c.key !== "all" ? c.label : "The Collection";
  }, [category]);

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="shop-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <div className="text-xs uppercase tracking-[0.32em] text-primary">Shop</div>
          <h1 className="font-serif text-5xl md:text-6xl mt-4 tracking-tight" data-testid="shop-heading">
            {heading}.
          </h1>
        </div>

        <form onSubmit={onSubmitSearch} className="flex items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2 border-b border-border px-2 py-2 w-full md:w-80">
            <Search size={16} className="text-muted-foreground" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search fragrances, notes…"
              data-testid="shop-search-input"
              className="bg-transparent flex-1 outline-none text-sm placeholder:text-muted-foreground"
            />
          </div>
          <button type="submit" data-testid="shop-search-btn" className="text-xs uppercase tracking-[0.22em] text-muted-foreground hover:text-primary">
            Go
          </button>
        </form>
      </div>

      {/* Filter bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-b border-border py-4 mb-14">
        <div className="flex items-center gap-6 overflow-x-auto">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              onClick={() => updateParam("category", c.key)}
              data-testid={`filter-cat-${c.key}`}
              className={`text-xs uppercase tracking-[0.22em] whitespace-nowrap transition-colors ${
                category === c.key ? "text-primary" : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-[0.22em] text-muted-foreground">Sort</span>
          <select
            value={sort}
            onChange={(e) => updateParam("sort", e.target.value)}
            data-testid="shop-sort-select"
            className="bg-transparent border border-border text-sm px-3 py-2 uppercase tracking-widest text-xs focus:outline-none focus:border-primary"
          >
            {SORTS.map((s) => (
              <option key={s.key} value={s.key} className="bg-background">
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm" data-testid="shop-loading">Composing…</div>
      ) : products.length === 0 ? (
        <div className="text-muted-foreground text-sm py-12 text-center" data-testid="shop-empty">
          No fragrances match your search.
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14" data-testid="shop-grid">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      )}
    </div>
  );
}
